import './aws-config';
import { Component } from 'react';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { MaintenanceProvider, useMaintenance } from './contexts/MaintenanceContext';
import MaintenanceView from './components/MaintenanceView';
import Navigation from './components/Navigation';
import RankingsView from './views/RankingsView';
import BaseballComingSoonView from './views/BaseballComingSoonView';
import LoginView from './views/LoginView';
import ProfileView from './views/ProfileView';
import DraftView from './views/DraftView';
import DraftHubView from './views/DraftHubView';
import DraftJoinView from './views/DraftJoinView';
import DraftCommissionerSetupView from './views/DraftCommissionerSetupView';
import DraftLobbyView from './views/DraftLobbyView';
import DraftTvView from './views/DraftTvView';
import LeagueHubView from './views/LeagueHubView';
import LeagueCreateView from './views/LeagueCreateView';
import LeagueView from './views/LeagueView';
import YahooCallbackView from './views/YahooCallbackView';
import AboutView from './views/AboutView';
import OpponentManager from './views/OpponentManager';
import OpponentDetail from './views/OpponentDetail';
import FaqView from './views/FaqView';
import GlossaryView from './views/GlossaryView';
import DraftAssistantView from './views/DraftAssistantView';
import WaiverWireStrategyView from './views/WaiverWireStrategyView';
import ConsensusRankingsExplainedView from './views/ConsensusRankingsExplainedView';
import PprVsStandardScoringView from './views/PprVsStandardScoringView';
import SnakeDraftStrategyView from './views/SnakeDraftStrategyView';
import { ORGANIZATION_SCHEMA, SOFTWARE_APPLICATION_SCHEMA, toJsonLd } from './schema';
import './App.css';

export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error('Uncaught render error:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <h2>Something went wrong</h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: 24 }}>
            An unexpected error occurred. Please refresh the page to continue.
          </p>
          <button className='btn btn-primary' onClick={() => window.location.reload()}>
            Refresh page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function AppShell() {
  const { maintenanceMode, message } = useMaintenance();

  return (
    <ErrorBoundary>
      {/*
        No <title>/<meta name="description"> in this app-level <Helmet> on purpose --
        two simultaneously-mounted <Helmet> instances that each set one don't dedupe under
        react-helmet-async 3.x + React 19 (confirmed via real prerendered output: both
        titles showed up, not just the deeper one). Each public view (RankingsView.js,
        AboutView.jsx, etc.) owns its own single <Helmet> for title/description/canonical/
        OG instead; public/index.html's static <title>/description is the fallback for
        routes that render none. JSON-LD <script> tags don't have that conflict (Helmet
        only dedupes by matching tag content, not "one script slot per page"), so the
        sitewide Organization/SoftwareApplication schema lives here safely alongside each
        page's own additional schema (e.g. FAQPage on /faq).
      */}
      <Helmet>
        <script type='application/ld+json'>
          {toJsonLd(ORGANIZATION_SCHEMA)}
        </script>
        <script type='application/ld+json'>
          {toJsonLd(SOFTWARE_APPLICATION_SCHEMA)}
        </script>
      </Helmet>
      {maintenanceMode ? (
        <MaintenanceView message={message} />
      ) : (
        <div className='app'>
          <Navigation />
          <main className='main-content'>
            <Routes>
              <Route path='/' element={<RankingsView />} />
              <Route path='/baseball' element={<BaseballComingSoonView />} />
              <Route path='/login' element={<LoginView />} />
              <Route path='/profile' element={<ProfileView />} />
              <Route path='/draft' element={<DraftHubView />} />
              <Route path='/draft/new' element={<DraftView />} />
              <Route path='/draft/join' element={<DraftJoinView />} />
              <Route path='/draft/join/:code' element={<DraftJoinView />} />
              <Route path='/draft/commissioner/new' element={<DraftCommissionerSetupView />} />
              <Route path='/draft/:draftId/lobby' element={<DraftLobbyView />} />
              <Route path='/draft/:draftId/tv' element={<DraftTvView />} />
              <Route path='/draft/:draftId' element={<DraftView />} />
              <Route path='/league' element={<LeagueHubView />} />
              <Route path='/league/new' element={<LeagueCreateView />} />
              <Route path='/league/:leagueId' element={<LeagueView />} />
              <Route path='/yahoo/callback' element={<YahooCallbackView />} />
              <Route path='/about' element={<AboutView />} />
              <Route path='/opponents' element={<OpponentManager />} />
              <Route path='/opponents/:id' element={<OpponentDetail />} />
              <Route path='/faq' element={<FaqView />} />
              <Route path='/glossary' element={<GlossaryView />} />
              <Route path='/draft-assistant' element={<DraftAssistantView />} />
              <Route path='/waiver-wire-strategy' element={<WaiverWireStrategyView />} />
              <Route path='/how-rankings-are-calculated' element={<ConsensusRankingsExplainedView />} />
              <Route path='/ppr-vs-standard-scoring' element={<PprVsStandardScoringView />} />
              <Route path='/snake-draft-strategy' element={<SnakeDraftStrategyView />} />
            </Routes>
          </main>
        </div>
      )}
    </ErrorBoundary>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ThemeProvider>
          <MaintenanceProvider>
            <AuthProvider>
              <AppShell />
            </AuthProvider>
          </MaintenanceProvider>
        </ThemeProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
